import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";
import {
  FacebookShareButton,
  LineShareButton,
  TwitterShareButton,
} from "react-share";
import { Button } from "@components/Button";
import { CommonHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import FavoriteTeamButton from "@components/FavoriteTeamButton";
import { ImageFlux } from "@components/ImageFlux";
import Markdown from "@components/Markdown";
import { MatchCard } from "@components/MatchCard";
import { ShopCardSmall } from "@components/ShopCardListItem/ShopCardSmall";
import { TeamLatestNews } from "@components/TeamLatestNews";
import useIsPcByUA from "@hooks/useIsPcByUA";
import { useShare } from "@hooks/useShare";
import { queryForSSP } from "@libs/apolloClient";
import { paths } from "@libs/paths";
import { toLocaleStr } from "@libs/utils/date";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import {
  News_NewsArticleDocument as NewsArticleDocument,
  useNews_NewsArticleShopsQuery as useNewsArticleShopsQuery,
  useNews_TeamLatestNewsArticlesQuery as useTeamLatestNewsArticlesQuery,
} from "./__generated__/queries";
import styles from "./index.module.css";
import type { News_NewsArticleQueryResult as NewsArticleQueryResult } from "./__generated__/queries";
import type { ApolloError } from "@apollo/client";
import type { GetServerSideProps, NextPage } from "next";

type Props = {
  data?: NewsArticleQueryResult["data"];
  error?: ApolloError;
};

const NewsArticle: NextPage<Props> = ({ data, error }) => {
  const { isPcByUA } = useIsPcByUA.useContainer();
  const { asPath } = useRouter();
  const url = process.env.APP_HOST + asPath;
  const newsArticle = data?.newsArticle;

  const { data: newsArticleShopsData } = useNewsArticleShopsQuery({
    variables: {
      newsArticleId: newsArticle?.id || 0,
      matchIds: newsArticle?.matches.map((m) => m.id),
    },
    skip: !newsArticle?.id,
    onError: console.error,
  });

  const newsArticleShops = newsArticleShopsData?.newsArticle.shops || [];

  const { data: relatedArticlesData } = useTeamLatestNewsArticlesQuery({
    variables: {
      masterTeamSlug: newsArticle?.masterTeam?.slug ?? "",
      newsArticleId: newsArticle?.id,
    },
    skip: !newsArticle?.masterTeam,
    onError: console.error,
  });

  // フッター固定ボタン表示時にページ下部に余白を設けるために、
  // Layoutコンポーネントのスタイルで定義しているクラスの追加削除でレイアウト調整をします
  useEffect(() => {
    const $footer = document.querySelector("footer");
    if (!$footer) {
      return;
    }

    if (newsArticle?.buttonLabel) {
      $footer.classList.add("isNewsFixdedButton");
    } else {
      $footer.classList.remove("isNewsFixdedButton");
    }
  }, [newsArticle?.buttonLabel]);

  const { handleShareClick } = useShare(newsArticle?.title ?? "", url);

  if (error) {
    console.error(error);
  }

  if (!newsArticle) {
    return null;
  }

  const pageTitle =
    newsArticle.title +
    (newsArticle.masterTeam
      ? `- ${newsArticle.masterTeam.name}関連のニュース`
      : "");

  return (
    <CommonLayout hasFooter hasFixedButtonSpace>
      <CommonHead
        title={pageTitle}
        description={newsArticle.metaDescription}
        ogImage={newsArticle.ogpImageUrl}
      />
      <div className={styles.teamNewsWrapper}>
        <div className={styles.teamNewsContentWidth}>
          <ul className={styles.breadcrumb}>
            <li>
              <Link href={paths.home}>トップ</Link>
            </li>
            {newsArticle.masterTeam ? (
              <li>
                <Link
                  href={paths.team(
                    newsArticle.masterTeam.masterSportGenre.slug || "",
                    newsArticle.masterTeam.slug || ""
                  )}
                  onClick={() => {
                    if (newsArticle.masterTeam?.id) {
                      GTMEvent(
                        GTMEvents.toTeamPagePressed(
                          "breadcrumb",
                          newsArticle.masterTeam.id
                        )
                      );
                    }
                  }}
                >
                  {newsArticle.masterTeam.name}
                </Link>
              </li>
            ) : (
              <li>
                <Link href={paths.newsArticles}>すべてのニュース</Link>
              </li>
            )}
            <li>ニュース</li>
          </ul>
          <article className={styles.teamNewsArticle}>
            <div className={styles.articleHeader}>
              <h1 className={styles.articleTitle}>{newsArticle.title}</h1>
              <div className={styles.articleHeaderInfo}>
                <div className={styles.articleDate}>
                  <span className={styles.date}>
                    {toLocaleStr(newsArticle.startsAt)}
                  </span>
                </div>
                <ul className={styles.snsShare}>
                  {isPcByUA ? (
                    <>
                      <li>
                        <TwitterShareButton
                          url={url}
                          title={newsArticle.title}
                          onClick={() => {
                            GTMEvent(GTMEvents.shareButtonPressed("twitter"));
                          }}
                        >
                          <img src="/img/button_x.svg" alt="X" />
                        </TwitterShareButton>
                      </li>
                      <li>
                        <FacebookShareButton
                          url={url}
                          onClick={() => {
                            GTMEvent(GTMEvents.shareButtonPressed("facebook"));
                          }}
                        >
                          <img src="/img/button_facebook.svg" alt="Facebook" />
                        </FacebookShareButton>
                      </li>
                      <li>
                        <LineShareButton
                          url={url}
                          title={newsArticle.title}
                          onClick={() => {
                            GTMEvent(GTMEvents.shareButtonPressed("line"));
                          }}
                        >
                          <img src="/img/button_line.svg" alt="Line" />
                        </LineShareButton>
                      </li>
                    </>
                  ) : (
                    <li>
                      <button
                        onClick={() => {
                          GTMEvent(GTMEvents.shareButtonPressed("native"));
                          handleShareClick();
                        }}
                      >
                        <img src="/img/button_share.svg" alt="Share" />
                      </button>
                    </li>
                  )}
                </ul>
              </div>
              {newsArticle.masterTeam?.topPriorityTournament
                ?.shouldShowDazn && (
                <div className={styles.copyright}>
                  <img
                    className={styles.logoDAZN}
                    src="/img/logo_dazn.svg"
                    alt=""
                  />
                  Organized by DAZN
                </div>
              )}
            </div>
            <div className={styles.articleBody}>
              <div className={styles.articleMainVisual}>
                <ImageFlux
                  imageType="news"
                  url={newsArticle.imageUrl}
                  alt={newsArticle.imageAlt}
                />
              </div>
              <Markdown text={newsArticle.body} />
              {newsArticle.matches.length > 0 && (
                <div className={styles.articleContents}>
                  <h2 className="c-contentTitle c-contentTitle--fz23 ">
                    対象の試合
                  </h2>
                  <div className={styles.matchScheduleList}>
                    {newsArticle.matches.map((match) => (
                      <MatchCard
                        match={match}
                        key={match.id}
                        showsTournamentName={true}
                      />
                    ))}
                  </div>
                </div>
              )}
              {newsArticleShops && newsArticleShops.length > 0 && (
                <div className={styles.articleContents}>
                  <h2 className="c-contentTitle c-contentTitle--fz23">
                    対象のお店
                  </h2>
                  <div className={styles.shopListItemList}>
                    {newsArticleShops.map((shop) => (
                      <ShopCardSmall shop={shop} key={shop.uuid} fixedWidth />
                    ))}
                  </div>
                </div>
              )}
              {newsArticle.buttonLabel && (
                <Link href={newsArticle.buttonLinkUrl ?? ""}>
                  <Button
                    className={styles.ctaBtn}
                    styleType="filled"
                    colorType="primary"
                  >
                    {newsArticle.buttonLabel}
                  </Button>
                </Link>
              )}
              {newsArticle.note && (
                <dl className={styles.noticeBlock}>
                  <dt>注意事項</dt>
                  <Markdown text={newsArticle.note} />
                </dl>
              )}
            </div>
          </article>

          {newsArticle.masterTeam && (
            <div className={styles.artcleFooter}>
              <div className={styles.artcleFooterInner}>
                <Link
                  href={paths.team(
                    newsArticle.masterTeam.masterSportGenre.slug || "",
                    newsArticle.masterTeam.slug || ""
                  )}
                  className={styles.team}
                  onClick={() => {
                    if (newsArticle.masterTeam?.id) {
                      GTMEvent(
                        GTMEvents.toTeamPagePressed(
                          "next_to_favorite_button",
                          newsArticle.masterTeam.id
                        )
                      );
                    }
                  }}
                >
                  <div className={styles.teamIcon}>
                    <img
                      src={newsArticle.masterTeam.iconPath || ""}
                      alt={newsArticle.masterTeam.name}
                    />
                  </div>
                  <div className={styles.teamName}>
                    {newsArticle.masterTeam.name}
                  </div>
                </Link>
                <div className={styles.teamAddButton}>
                  <FavoriteTeamButton teamId={newsArticle.masterTeam.id} />
                </div>
              </div>
            </div>
          )}

          {newsArticle.masterTeam &&
            relatedArticlesData &&
            relatedArticlesData.teamLatestNewsArticles.length > 0 && (
              <div className={styles.relatedArticles}>
                <TeamLatestNews
                  team={newsArticle.masterTeam}
                  newsArticles={relatedArticlesData.teamLatestNewsArticles}
                  portrait={false}
                />
              </div>
            )}
        </div>

        {newsArticle.buttonLabel && (
          <div className={`${styles.fixedCtaButton} md:none`}>
            <Link href={newsArticle.buttonLinkUrl ?? ""}>
              <Button
                className={styles.ctaBtn}
                styleType="filled"
                colorType="primary"
              >
                {newsArticle.buttonLabel}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params } = context;
  return queryForSSP({
    query: NewsArticleDocument,
    variables: {
      newsArticleId: Number(params?.id),
    },
  });
};

export default NewsArticle;
